/**
 * ClinVea-network Referral workflow — backs /clinical-referrals on the backend
 * (ClinicalReferralController). NOT the same as `api.referrals` (modules/referrals.ts), which is
 * the unrelated marketing "Patient Referral Program" at /referrals. Same word, different feature —
 * see ClinicalReferralController's class comment before touching either.
 */
import { fetchApi } from '../client';
import { buildQS } from '../helpers';

export type ReferralDestinationType = 'CLINVEA' | 'EXTERNAL';

export type ReferralStatus =
  | 'DRAFT' | 'SUBMITTED' | 'SENT' | 'DELIVERED' | 'RECEIVED' | 'UNDER_REVIEW'
  | 'ACCEPTED' | 'DECLINED' | 'NEEDS_MORE_INFORMATION' | 'SCHEDULED'
  | 'PATIENT_SEEN' | 'REPORT_PENDING' | 'REPORT_RECEIVED' | 'COMPLETED'
  | 'CANCELLED' | 'EXPIRED';

export type ClinicalUrgency = 'ROUTINE' | 'URGENT' | 'EMERGENCY';

export type RecordShareCategory =
  | 'CLINICAL_SUMMARY' | 'DIAGNOSES' | 'MEDICATIONS' | 'ALLERGIES' | 'LAB_RESULTS'
  | 'IMAGING' | 'PRESCRIPTIONS' | 'CONSULTATION_NOTES' | 'PREVIOUS_PROCEDURES'
  | 'VITALS' | 'VISITS' | 'ENTIRE_HISTORY';

export interface RecordShareSelection {
  id: string;
  categories: RecordShareCategory[];
  dateFrom?: string | null;
  dateTo?: string | null;
  createdBy: string;
  createdAt: string;
}

export interface Referral {
  id: string;
  referralNumber: string;
  patientId: string;
  patientName?: string;
  patientMobile?: string;
  patientDateOfBirth?: string;
  patientAge?: number;
  patientGender?: string;

  referringClinicId: string;
  referringClinicName?: string;
  referringProviderId?: string;
  referringProviderName?: string;

  destinationType: ReferralDestinationType;
  destinationOrganizationId?: string;
  destinationOrganizationName?: string;
  destinationProviderId?: string;
  destinationProviderName?: string;
  externalOrganizationId?: string;
  externalOrganizationName?: string;
  externalProviderId?: string;
  externalProviderName?: string;

  specialty?: string;
  referralReason: string;
  clinicalSummary?: string;
  urgency: ClinicalUrgency;
  requestedService?: string;
  status: ReferralStatus;
  appointmentId?: string;

  createdAt: string;
  submittedAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  scheduledAt?: string;
  patientSeenAt?: string;
  reportReceivedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  expiresAt?: string;

  incoming: boolean;
  allowedNextStatuses: ReferralStatus[];
  recordShare?: RecordShareSelection | null;
  messageCount: number;
}

export interface ReferralDashboard {
  outgoingTotal: number;
  outgoingPendingDecision: number;
  outgoingNeedsMoreInfo: number;
  outgoingScheduled: number;
  outgoingAwaitingReport: number;
  outgoingCompleted: number;
  incomingTotal: number;
  incomingNeedsReview: number;
  incomingAccepted: number;
  incomingScheduled: number;
  emergencyOrUrgentOpen: number;
}

export interface ReferralStatusHistoryEntry {
  id: string;
  referralId: string;
  fromStatus: ReferralStatus | null;
  toStatus: ReferralStatus;
  changedBy: string;
  reason?: string | null;
  changedAt: string;
}

export interface ReferralMessage {
  id: string;
  referralId: string;
  senderId: string;
  message: string;
  sentAt: string;
  readAt?: string | null;
}

export interface DestinationClinic {
  id: string;
  name: string;
  city?: string;
  state?: string;
}

export interface DestinationDoctor {
  id: string;
  name: string;
  specialization?: string;
  departmentId?: string;
}

export interface ExternalOrganization {
  id: string;
  name: string;
  organizationType?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  notes?: string;
  isActive: boolean;
}

export interface ExternalProvider {
  id: string;
  providerName: string;
  externalOrganizationId?: string;
  specialty?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  contactPerson?: string;
  notes?: string;
  isActive: boolean;
}

export interface ExternalOrganizationCreateRequest {
  name: string;
  organizationType?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  notes?: string;
}

export interface ExternalProviderCreateRequest {
  providerName: string;
  externalOrganizationId?: string;
  specialty?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  contactPerson?: string;
  notes?: string;
}

export interface ReferralCreateRequest {
  patientId: string;
  referringProviderId?: string;
  destinationType: ReferralDestinationType;
  destinationOrganizationId?: string;
  destinationProviderId?: string;
  externalOrganizationId?: string;
  externalProviderId?: string;
  specialty?: string;
  referralReason: string;
  clinicalSummary?: string;
  urgency: ClinicalUrgency;
  requestedService?: string;
  recordShareCategories?: RecordShareCategory[];
  recordShareDateFrom?: string;
  recordShareDateTo?: string;
  submit: boolean;
}

const BASE = '/api/clinical-referrals';

export const clinicalReferralsModule = {
  /** `patientId` narrows the clinic-scoped list server-side — used by the consultation Referrals tab. */
  listOutgoing: (status?: ReferralStatus, patientId?: string) =>
    fetchApi<Referral[]>(`${BASE}${buildQS({ status, patientId })}`),

  listIncoming: () =>
    fetchApi<Referral[]>(`${BASE}/incoming`),

  getDashboard: () =>
    fetchApi<ReferralDashboard>(`${BASE}/dashboard`),

  searchDestinationClinics: (q: string) =>
    fetchApi<DestinationClinic[]>(`${BASE}/destinations/clinics${buildQS({ q })}`),

  listDestinationDoctors: (destinationClinicId: string) =>
    fetchApi<DestinationDoctor[]>(`${BASE}/destinations/clinics/${destinationClinicId}/doctors`),

  get: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}`),

  getTimeline: (id: string) =>
    fetchApi<ReferralStatusHistoryEntry[]>(`${BASE}/${id}/timeline`),

  create: (data: ReferralCreateRequest) =>
    fetchApi<Referral>(BASE, { method: 'POST', body: JSON.stringify(data) }),

  submit: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/submit`, { method: 'POST' }),

  accept: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/accept`, { method: 'POST' }),

  decline: (id: string, reason?: string) =>
    fetchApi<Referral>(`${BASE}/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),

  requestMoreInfo: (id: string, reason: string) =>
    fetchApi<Referral>(`${BASE}/${id}/request-more-info`, { method: 'POST', body: JSON.stringify({ reason }) }),

  schedule: (id: string, data: { appointmentId?: string; scheduledAt?: string }) =>
    fetchApi<Referral>(`${BASE}/${id}/schedule`, { method: 'POST', body: JSON.stringify(data) }),

  markPatientSeen: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/patient-seen`, { method: 'POST' }),

  markReportPending: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/report-pending`, { method: 'POST' }),

  receiveReport: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/report-received`, { method: 'POST' }),

  complete: (id: string) =>
    fetchApi<Referral>(`${BASE}/${id}/complete`, { method: 'POST' }),

  cancel: (id: string, reason?: string) =>
    fetchApi<Referral>(`${BASE}/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  listMessages: (id: string) =>
    fetchApi<ReferralMessage[]>(`${BASE}/${id}/messages`),

  addMessage: (id: string, message: string) =>
    fetchApi<ReferralMessage>(`${BASE}/${id}/messages`, { method: 'POST', body: JSON.stringify({ message }) }),

  getRecordShare: (id: string) =>
    fetchApi<RecordShareSelection | null>(`${BASE}/${id}/record-share`),

  setRecordShare: (id: string, data: { categories: RecordShareCategory[]; dateFrom?: string; dateTo?: string }) =>
    fetchApi<RecordShareSelection>(`${BASE}/${id}/record-share`, { method: 'POST', body: JSON.stringify(data) }),

  // ── External directory (providers/organizations outside ClinVea) ─────────

  searchExternalOrganizations: (q?: string) =>
    fetchApi<ExternalOrganization[]>(`${BASE}/external/organizations${buildQS({ q })}`),

  createExternalOrganization: (data: ExternalOrganizationCreateRequest) =>
    fetchApi<ExternalOrganization>(`${BASE}/external/organizations`, { method: 'POST', body: JSON.stringify(data) }),

  listExternalProvidersForOrganization: (organizationId: string) =>
    fetchApi<ExternalProvider[]>(`${BASE}/external/organizations/${organizationId}/providers`),

  searchExternalProviders: (q: string) =>
    fetchApi<ExternalProvider[]>(`${BASE}/external/providers${buildQS({ q })}`),

  createExternalProvider: (data: ExternalProviderCreateRequest) =>
    fetchApi<ExternalProvider>(`${BASE}/external/providers`, { method: 'POST', body: JSON.stringify(data) }),
};
